import React, { useState, useMemo, useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { getResourceNameFromResponseBody } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import { useIsDynatraceEnhancedStatusCheck } from 'components/status-checks/hooks/use-is-dynatrace-enhanced-status-check'
import { type EvaluationResults } from 'types/api/status-checks/evaluation'
import { type EndpointResponse } from 'types/api/status-checks/status-check'

import type { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'

export interface StatusCheckDetailsContextValues {
  endpointResponse: EndpointResponse | undefined
  isStatusCheckConnectionValidated: boolean
  isStatusCheckEvaluationValidated: boolean
  isTestStatusCheckDisabled: boolean
  isTestSuccessEvaluationDisabled: boolean
  serviceName?: string
  shouldDisplayPredicateConfiguration: boolean
  handleSuccess: (
    endpointResponse: EndpointResponse,
    evaluationResults?: EvaluationResults
  ) => Promise<void>
  handleTestConnectionError: () => void
  setEndpointResponse: (endpointResponse: EndpointResponse | undefined) => void
  setIsStatusCheckConnectionValidated: (arg: boolean) => void
  setIsStatusCheckEvaluationValidated: (arg: boolean) => void
}

export const StatusCheckDetailsContext = React.createContext<
  StatusCheckDetailsContextValues | undefined
>(undefined)

const fieldsToWatch = [
  'evaluationConfiguration.responseBodyEvaluation.predicates',
  'externalIntegration',
  'statusCheckName'
] as const

export const StatusCheckDetailsProvider: React.FC<{
  serviceName?: string
}> = ({ children, serviceName = '' }) => {
  const [endpointResponse, setEndpointResponse] = useState<EndpointResponse>()
  const [
    isStatusCheckEvaluationValidated,
    setIsStatusCheckEvaluationValidated
  ] = useState(false)
  const [
    isStatusCheckConnectionValidated,
    setIsStatusCheckConnectionValidated
  ] = useState(false)

  const { control, getFieldState, setValue, trigger } =
    useFormContext<StatusCheckFormValues>()

  const [predicates, externalIntegration, statusCheckName] = useWatch({
    control,
    name: fieldsToWatch
  })

  const entityIdFieldState = getFieldState('dynatraceSelector.entityId')
  const k8sQueryFieldState = getFieldState('dynatraceSelector.k8sQuery')
  const { error: hasMonitorUrlError } = getFieldState('monitorUrl')
  const { error: hasRequestTimeoutError } = getFieldState(
    'evaluationConfiguration.requestTimeout'
  )

  // Field validation flags to ensure 'test status check' action is enabled
  const isDynatraceEnhancedStatusCheck = useIsDynatraceEnhancedStatusCheck()
  const isDynatraceEntityIdValid = !entityIdFieldState.error
  const isDynatraceK8sQueryValid = !k8sQueryFieldState.error
  const isMonitorUrlValid = !hasMonitorUrlError

  const isTestStatusCheckDisabled =
    // Default check here ensures the monitor URL field is valid
    (!isDynatraceEnhancedStatusCheck && !isMonitorUrlValid) ||
    // In the case of Dynatrace enhanced status checks, we must instead check for either a valid
    // 'entityId' or 'k8sQuery' field value.
    (isDynatraceEnhancedStatusCheck &&
      !isDynatraceEntityIdValid &&
      !isDynatraceK8sQueryValid)

  const isTestSuccessEvaluationDisabled =
    isTestStatusCheckDisabled ||
    !isStatusCheckConnectionValidated ||
    !endpointResponse ||
    Boolean(hasRequestTimeoutError)

  const shouldDisplayPredicateConfiguration = useMemo<boolean>(() => {
    if (!endpointResponse) return Boolean(predicates?.length)

    const { contentType } = endpointResponse ?? {}
    // We can probably assume here that we lead with '/', but cannot guarantee that this is
    // always placed at the end of the string. Example: PNI responses might be semi-colon delimited.
    const isJsonPattern = /\/json/i
    const isJsonContentType = contentType
      ? isJsonPattern.test(contentType)
      : false

    return isJsonContentType
  }, [endpointResponse, predicates])

  const handleSuccess = useCallback<
    StatusCheckDetailsContextValues['handleSuccess']
  >(
    async (endpointResponse, evaluationResults) => {
      setEndpointResponse(endpointResponse)
      setIsStatusCheckConnectionValidated(true)
      setIsStatusCheckEvaluationValidated((previousValue) =>
        Boolean(evaluationResults || previousValue)
      )

      // Pull the resource name from the response body
      const resourceName = externalIntegration
        ? getResourceNameFromResponseBody(endpointResponse, externalIntegration)
        : undefined

      // don't overwrite the status check name from the response body if it exists
      // in the form
      if (resourceName && !statusCheckName) {
        setValue('statusCheckName', resourceName)
      }

      // Validate monitorUrl on test success
      await trigger('monitorUrl')
    },
    [
      externalIntegration,
      statusCheckName,
      setIsStatusCheckConnectionValidated,
      setValue,
      trigger
    ]
  )

  const handleTestConnectionError = useCallback(() => {
    setIsStatusCheckConnectionValidated(false)
  }, [setIsStatusCheckConnectionValidated])

  const value = useMemo(() => {
    return {
      endpointResponse,
      isStatusCheckConnectionValidated,
      isStatusCheckEvaluationValidated,
      isTestStatusCheckDisabled,
      isTestSuccessEvaluationDisabled,
      serviceName,
      shouldDisplayPredicateConfiguration,
      handleSuccess,
      handleTestConnectionError,
      setEndpointResponse,
      setIsStatusCheckConnectionValidated,
      setIsStatusCheckEvaluationValidated
    }
  }, [
    endpointResponse,
    isStatusCheckConnectionValidated,
    isStatusCheckEvaluationValidated,
    isTestStatusCheckDisabled,
    isTestSuccessEvaluationDisabled,
    serviceName,
    shouldDisplayPredicateConfiguration,
    handleSuccess,
    handleTestConnectionError,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  ])

  return (
    <StatusCheckDetailsContext.Provider value={value}>
      {children}
    </StatusCheckDetailsContext.Provider>
  )
}

export function useStatusCheckDetailsContext() {
  const context = React.useContext(StatusCheckDetailsContext)

  if (context === undefined) {
    throw new Error(
      'useStatusCheckDetailsContext must be used within a StatusCheckDetailsProvider'
    )
  }

  return context
}
